<script setup lang="ts">
// First-class loading placeholder for the mandated `loading` state (BRIEF §3.7). Restores v3's
// `.skeleton-animate` (`style.css:358-380`) verbatim: a solid fill with a translucent band
// sweeping across every 1.5s, rather than Tailwind's generic opacity pulse — the pulse read as a
// barely-visible haze once paired with `surface-sunken` (a near-white insets colour, wrong fill
// for something meant to stand out against `surface`), which is why skeletons across the app
// looked washed out next to v3's. `lines > 1` renders a stacked text-block skeleton.
//
// `tone="strong"` is the one caller-visible variant: v3's paired bar/line chart skeletons drew
// the "current period" series a shade darker than "previous period" (`bg-gray-400` vs
// `bg-gray-300`) so a comparison pair reads as two series rather than one fused block —
// `ChartRoot`'s loading bars are the one caller that needs it.
//
// The ramp tones (`sunken` … `inverse`) are the exact cool-gray steps v3's `Skeletons/*.vue`
// painted with (`bg-gray-100` … `bg-gray-800`, see `semantic.css`), so a silhouette ported from v3
// keeps each element's original shade instead of collapsing onto the two neutral tones above.
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    width?: string
    height?: string
    radius?: string
    lines?: number
    tone?: 'base' | 'strong' | 'sunken' | 'soft' | 'medium' | 'bold' | 'inverse'
  }>(),
  { lines: 1, tone: 'base' },
)

const TONE_CLASS = {
  base: 'bg-[color:var(--ui-color-skeleton)]',
  strong: 'bg-[color:var(--ui-color-skeleton-strong)]',
  sunken: 'bg-skeleton-sunken',
  soft: 'bg-skeleton-soft',
  medium: 'bg-skeleton-medium',
  bold: 'bg-skeleton-bold',
  inverse: 'bg-skeleton-inverse',
} as const

const toneClass = computed(() => TONE_CLASS[props.tone])
</script>

<template>
  <span
    v-if="lines <= 1"
    class="ui-skeleton relative block h-4 w-full overflow-hidden rounded-sm"
    :class="toneClass"
    :style="{ width, height, borderRadius: radius }"
    aria-hidden="true"
  >
    <!-- v3 shimmered whole tiles (`skeleton-animate` on a card holding its own bars), so the
         sweep can run over placeholder children too; pass `height="auto"` for a content box. -->
    <slot />
  </span>
  <span v-else class="ui-skeleton-group flex flex-col gap-xs" aria-hidden="true">
    <span
      v-for="i in lines"
      :key="i"
      class="ui-skeleton relative block h-4 w-full overflow-hidden rounded-sm last:w-[70%]"
      :class="toneClass"
      :style="{ height, borderRadius: radius }"
    />
  </span>
</template>

<style scoped>
.ui-skeleton::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(
    to right,
    transparent 0%,
    var(--ui-color-skeleton-shimmer) 50%,
    transparent 100%
  );
  animation: ui-skeleton-shimmer 1.5s infinite;
}

@media (prefers-reduced-motion: reduce) {
  .ui-skeleton::after {
    animation: none;
  }
}

@keyframes ui-skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
